// Generated by GitHub Copilot
/**
 * Centralized Configuration Service
 *
 * Handles loading and caching of all application configuration data
 * including products, slack channels, delivery streams, and other settings.
 */

import configuration from "../connections/index";
import { authFetch } from "../utils/authFetch";
import { logError } from "../utils/errorLogging";

class ConfigurationService {
  constructor() {
    this.cache = {
      products: null,
      slack: null,
      deliveryStreams: null,
      configuration: null,
      lastFetched: null,
    };
    this.loading = false;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.debugMode = process.env.REACT_APP_DEBUG === "true";
  }

  /**
   * Get mock configuration data for debug mode
   * @returns {Object} Mock configuration data
   */
  getMockConfiguration() {
    return {
      products: [
        { label: "Mock Product 1", value: "Mock Product 1" },
        { label: "Mock Product 2", value: "Mock Product 2" },
        { label: "Mock Product 3", value: "Mock Product 3" },
        { label: "Mock Product 4", value: "Mock Product 4" },
        { label: "Mock Product 5", value: "Mock Product 5" },
      ],
      slack: {
        channels: [
          { label: "mock-channel-1", value: "mock-channel-1" },
          { label: "mock-channel-2", value: "mock-channel-2" },
          { label: "mock-notifications", value: "mock-notifications" },
          { label: "mock-alerts", value: "mock-alerts" },
        ],
        raw: {
          channels: [
            "mock-channel-1",
            "mock-channel-2",
            "mock-notifications",
            "mock-alerts",
          ],
        },
      },
      configuration: {
        slack: {
          channels: [
            "mock-channel-1",
            "mock-channel-2",
            "mock-notifications",
            "mock-alerts",
          ],
        },
      },
      portfolios: [
        { label: "Mock Portfolio A", value: "Mock Portfolio A" },
        { label: "Mock Portfolio B", value: "Mock Portfolio B" },
        { label: "Mock Portfolio C", value: "Mock Portfolio C" },
      ],
      deliveryStreams: [
        { label: "Mock Delivery Stream 1", value: "Mock Delivery Stream 1" },
        { label: "Mock Delivery Stream 2", value: "Mock Delivery Stream 2" },
        { label: "Mock Delivery Stream 3", value: "Mock Delivery Stream 3" },
      ],
      isLoading: false,
      lastFetched: Date.now(),
    };
  }

  /**
   * Load all configuration data from APIs
   * @returns {Promise<Object>} Combined configuration object
   */
  async loadConfiguration() {
    if (this.loading) {
      // Return cached data if already loading
      return this.getCachedConfiguration();
    }

    // In debug mode, return mock data immediately
    // if (this.debugMode) {
    //   console.log("Debug mode detected - using mock configuration data");
    //   const mockConfig = this.getMockConfiguration();
    //   this.cache.products = mockConfig.products;
    //   this.cache.slack = mockConfig.slack;
    //   this.cache.configuration = mockConfig.configuration;
    //   this.cache.lastFetched = mockConfig.lastFetched;
    //   return mockConfig;
    // }

    const now = Date.now();

    // Return cached data if it's still fresh
    if (
      this.cache.lastFetched &&
      now - this.cache.lastFetched < this.cacheTimeout
    ) {
      return this.getCachedConfiguration();
    }

    this.loading = true;

    try {
      // Fetch all configuration data in parallel
      const [productsResult, configResult] = await Promise.allSettled([
        this.fetchProducts(),
        this.fetchApplicationConfiguration(),
      ]);

      // Process products
      if (productsResult.status === "fulfilled") {
        this.cache.products = this.processProducts(productsResult.value);
      } else {
        logError("configurationService.loadConfiguration", productsResult.reason, {
          operation: "fetchProducts"
        });
        this.cache.products = [];
      }

      // Process application configuration (slack channels, etc.)
      if (configResult.status === "fulfilled") {
        this.cache.slack = this.processSlackChannels(configResult.value);
        this.cache.configuration = configResult.value;
      } else {
        logError("configurationService.loadConfiguration", configResult.reason, {
          operation: "fetchApplicationConfiguration"
        });
        this.cache.slack = { channels: [] };
        this.cache.configuration = {};
      }

      this.cache.lastFetched = now;

      return this.getCachedConfiguration();
    } catch (error) {
      logError("configurationService.loadConfiguration", error);
      return this.getCachedConfiguration(); // Return whatever we have cached
    } finally {
      this.loading = false;
    }
  }

  /**
   * Fetch products from API
   * @returns {Promise<Object>} Products API response
   */
  async fetchProducts() {
    const apiUrl = `${configuration.apiBase}/library/products`;
    const response = await authFetch(apiUrl, {
      credentials: "include", // Include cookies for authentication
    });

    if (!response.ok) {
      // Provide more specific error messages for common cases
      if (response.status === 401) {
        throw new Error("Authentication required for products API");
      } else if (response.status === 403) {
        throw new Error("Access forbidden for products API");
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Fetch application configuration from API
   * @returns {Promise<Object>} Configuration API response
   */
  async fetchApplicationConfiguration() {
    const apiUrl = `${configuration.apiBase}/library/configuration`;
    const response = await authFetch(apiUrl, {
      credentials: "include", // Include cookies for authentication
    });

    if (!response.ok) {
      // Provide more specific error messages for common cases
      if (response.status === 401) {
        throw new Error("Authentication required for configuration API");
      } else if (response.status === 403) {
        throw new Error("Access forbidden for configuration API");
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }
  /**
   * Process products into the format expected by components
   * @param {Object} apiResponse - Raw API response
   * @returns {Array} Processed product options
   */
  processProducts(apiResponse) {
    if (
      !apiResponse ||
      !apiResponse.products ||
      !Array.isArray(apiResponse.products)
    ) {
      console.warn("Invalid products API response format");
      return [];
    }

    return apiResponse.products.map((product) => ({
      label: product,
      value: product,
    }));
  }

  /**
   * Process slack channels into the format expected by components
   * @param {Object} configResponse - Raw configuration API response
   * @returns {Object} Processed slack configuration
   */
  processSlackChannels(configResponse) {
    if (
      !configResponse ||
      !configResponse.slack ||
      !configResponse.slack.channels
    ) {
      console.warn("Invalid slack configuration in API response");
      return { channels: [] };
    }

    const channelOptions = configResponse.slack.channels.map((channel) => ({
      label: channel,
      value: channel,
    }));

    return {
      channels: channelOptions,
      raw: configResponse.slack,
    };
  }

  /**
   * Get cached configuration data
   * @returns {Object} Current cached configuration
   */
  getCachedConfiguration() {
    return {
      products: this.cache.products || [],
      slack: this.cache.slack || { channels: [] },
      configuration: this.cache.configuration || {},
      portfolios: this.getPortfolioOptions(),
      deliveryStreams: this.getDeliveryStreamOptions(),
      lastFetched: this.cache.lastFetched,
      isLoading: this.loading,
    };
  }

  /**
   * Extract portfolio options from products
   * @returns {Array} Portfolio options
   */
  getPortfolioOptions() {
    // This would need to be implemented based on how portfolios are derived from products
    // For now, return empty array as placeholder
    return [];
  }

  /**
   * Extract delivery stream options from products
   * @returns {Array} Delivery stream options
   */
  getDeliveryStreamOptions() {
    // This would need to be implemented based on how delivery streams are derived
    // For now, return empty array as placeholder
    return [];
  }

  /**
   * Force refresh of configuration data
   * @returns {Promise<Object>} Fresh configuration data
   */
  async refresh() {
    this.cache.lastFetched = null;
    return await this.loadConfiguration();
  }

  /**
   * Check if configuration is loading
   * @returns {boolean} True if currently loading
   */
  isLoading() {
    return this.loading;
  }

  /**
   * Get age of cached data in milliseconds
   * @returns {number} Age in milliseconds, or null if no cached data
   */
  getCacheAge() {
    if (!this.cache.lastFetched) return null;
    return Date.now() - this.cache.lastFetched;
  }

  /**
   * Clear cached configuration data
   */
  clearCache() {
    this.cache = {
      products: null,
      slack: null,
      deliveryStreams: null,
      configuration: null,
      lastFetched: null,
    };
  }
}

// Create singleton instance
const configurationService = new ConfigurationService();

export default configurationService;
